import React from 'react'
import {X} from 'lucide-react'
import {Combobox} from '@/components/ui/combobox'
import type {ComboboxOption} from '@/components/ui/combobox'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {MutedText} from '@/components/shared/MutedText'
import {useI18n} from '@/i18n'

export interface AssociationColumn<T> {
    header: string
    render: (item: T) => React.ReactNode
    className?: string
}

interface AssociationEditorProps<T> {
    items: T[]
    onSearch: (query: string) => void
    searchResults: T[]
    onAdd: (item: T) => void
    onRemove: (item: T) => void
    columns: AssociationColumn<T>[]
    getKey: (item: T) => string
    getLabel: (item: T) => string
    searchPlaceholder?: string
    emptyMessage?: string
    isLoading?: boolean
    disabled?: boolean
    testId?: string
    renderItemExtra?: (item: T) => React.ReactNode
    /**
     * Hide the table header row. Use when the card title + single-column layout
     * already convey the column meaning and a repeat header adds noise.
     */
    hideHeader?: boolean
    /**
     * Optional row click handler. When set, each data row becomes clickable
     * (cursor-pointer + hover bg). The remove-button click is isolated via
     * stopPropagation so removing an item doesn't fire the row handler.
     */
    onRowClick?: (item: T) => void
}

export function AssociationEditor<T>({
    items,
    onSearch,
    searchResults,
    onAdd,
    onRemove,
    columns,
    getKey,
    getLabel,
    searchPlaceholder,
    emptyMessage,
    isLoading = false,
    disabled = false,
    testId,
    renderItemExtra,
    hideHeader = false,
    onRowClick,
}: AssociationEditorProps<T>) {
    const i18n = useI18n()
    const resolvedSearchPlaceholder = searchPlaceholder ?? i18n.associationEditor.searchPlaceholder
    const resolvedEmptyMessage = emptyMessage ?? i18n.associationEditor.emptyMessage

    const selectedKeys = new Set(items.map(getKey))
    const availableOptions: ComboboxOption[] = searchResults
        .filter((r) => !selectedKeys.has(getKey(r)))
        .map((r) => ({
            value: getKey(r),
            label: getLabel(r),
        }))

    const handleSelect = (value: string | null) => {
        if (!value) return
        const item = searchResults.find((r) => getKey(r) === value)
        if (item) {
            onAdd(item)
            onSearch('')
        }
    }

    if (isLoading) {
        return (
            <div data-testid={testId ? `${testId}-loading` : 'association-editor-loading'}>
                <Skeleton className="h-10 w-full mb-3" />
                <Skeleton className="h-8 w-full mb-2" />
                <Skeleton className="h-8 w-full mb-2" />
                <Skeleton className="h-8 w-3/4" />
            </div>
        )
    }

    return (
        <div data-testid={testId}>
            {!disabled && (
                <div className="mb-3">
                    <Combobox
                        options={availableOptions}
                        value={null}
                        onValueChange={handleSelect}
                        onSearch={onSearch}
                        placeholder={resolvedSearchPlaceholder}
                        data-testid={testId ? `${testId}-search` : undefined}
                    />
                </div>
            )}

            {items.length === 0 ? (
                <MutedText size="sm" data-testid={testId ? `${testId}-empty` : 'association-editor-empty'}>
                    {resolvedEmptyMessage}
                </MutedText>
            ) : (
                <Table data-testid={testId ? `${testId}-selected` : undefined}>
                    {!hideHeader && (
                        <TableHeader>
                            <TableRow>
                                {columns.map((col, idx) => (
                                    <TableHead key={idx} className={col.className} data-testid={`col-${idx}`}>
                                        {col.header}
                                    </TableHead>
                                ))}
                                {!disabled && <TableHead className="w-12 pr-2" data-testid="col-actions" />}
                            </TableRow>
                        </TableHeader>
                    )}
                    <TableBody>
                        {items.map((item) => {
                            const key = getKey(item)
                            const label = getLabel(item)
                            const extra = renderItemExtra?.(item)
                            const colSpan = columns.length + (disabled ? 0 : 1)
                            return (
                                <React.Fragment key={key}>
                                    <TableRow
                                        className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : undefined}
                                        onClick={onRowClick ? () => onRowClick(item) : undefined}
                                    >
                                        {columns.map((col, idx) => (
                                            <TableCell key={idx} className={col.className}>
                                                {col.render(item)}
                                            </TableCell>
                                        ))}
                                        {!disabled && (
                                            <TableCell className="w-12 pr-2 text-right">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); onRemove(item) }}
                                                    aria-label={i18n.associationEditor.removeAriaLabel({label})}
                                                    data-testid={testId ? `${testId}-remove-${key}` : `association-editor-remove-${key}`}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                    {extra && (
                                        <TableRow>
                                            <TableCell colSpan={colSpan} className="pt-3 pb-3">
                                                {extra}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </TableBody>
                </Table>
            )}
        </div>
    )
}
